import type { BaseOptions } from '../../common/types';
import type { CoreTypes } from '../../core';
import type { MixedScheduleData } from '../make-connected';
import type { Types } from './';
import type { AlgorithmParameters, AlgorithmWeightParameters } from './algorithm-parameters';


export type ToInput = MixedScheduleData;

type PartialScheduleOptions<IDs extends Array<string> | Set<string>> = {
  /** @description undefined means all included */
  includedEvents?:       IDs;
  /** @description undefined means all included */
  includedLocations?:    IDs;
  omittedEventsHandling: 'ignore' | 'freeze';
};

export interface ToOptions extends BaseOptions {
  /**
   * Includes some metadata about entities. Currently this only includes name but might be extended in the future.
   * This is useful when running the algorithm locally from its GUI.
   * @default false
   */
  includeEntityMeta?: boolean;

  /**
   * If true, the output will include the full core data. This enables the resulting output file to be uploaded as a new schedule.
   * @default false
   */
  appendCoreData?: boolean;

  /**
   * If true, the output will include current configuration of events and locked times as `output` entry.
   * @default false
   */
  appendOutput?: boolean;

  /**
   * Allows to specify that only a subset of events and/or locations should be included in the schedule optimization, and how the omitted events should be handled.
   */
  partialScheduleOptions?: PartialScheduleOptions<Array<string> | Set<string>>

  /**
   * The weight parameters to be used by the algorithm. One may also specify `false` for any of the parameters to disable the corresponding feature.
   */
  algorithmWeightParameters?: AlgorithmWeightParameters;

  /**
   * The time multiplier to be used by the algorithm. A number twice as large implies that the algorithm will spend twice as much time optimizing the schedule.
   * @default 1
   */
  computeTimeMultiplier?: number;
}

export interface ParsedToOptions extends Omit<ToOptions, 'partialScheduleOptions'> {
  partialScheduleOptions?: PartialScheduleOptions<Set<string>>
}

export const structure = 'RS/algorithm-6.0.0';
export type Structure = typeof structure;
export interface ToOutput {
  meta:      {
    structure?: Structure;
    division:   Pick<CoreTypes.Deep.Division, 'displayName' | 'start' | 'end'>;
  };
  algorithmParameters: AlgorithmParameters;
  score?:              [number, number, number, number];
  input:               Types.scheduleData;
  output?:             Types.configuration[];
  coreData?:           Partial<CoreTypes.SerializedWithOptionalId.Schedule>;
}